<template>
  <div class="page-container audits-page">
    <portal to="page-name">Auditoria</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Auditoria</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-3 col-md-5 col-sm-12 form-group"
               :class="{'has-error': $v.filters.startDate.$error || $v.filters.endDate.$error}">
            <label class="form-label">
              Período máximo 30 dias
            </label>
            <st-date-period
              :start-date.sync="filters.startDate"
              :end-date.sync="filters.endDate"
              :max-months="1"
              :start-date-validator="$v.filters.startDate"
              :end-date-validator="$v.filters.endDate"
              @blur:startDate="$v.filters.startDate.$touch()"
              @blur:endDate="$v.filters.endDate.$touch()"
            />
          </div>
          <div class="column col-3 col-md-7 col-sm-12 form-group">
            <label class="form-label">Registro</label>
            <select class="form-select" v-model="filters.entity">
              <option value="">[Todos os registros]</option>
              <option v-for="(item, i) in entities"
                      :value="item.key" :key="i">{{ item.name }}</option>
            </select>
          </div>
          <div class="column col-2 col-md-5 col-sm-12 form-group">
            <label class="form-label">Ação</label>
            <select class="form-select" v-model="filters.action">
              <option value="">[Todas as ações]</option>
              <option v-for="(item, i) in actions"
                      :value="item.key" :key="i">{{ item.name }}</option>
            </select>
          </div>
          <div class="column col-sm-12 form-group">
            <label class="form-label">Busca</label>
            <input type="text" class="form-input"
                   v-model="filters.search"
                   placeholder="Usuário responsável ou identificação do registro">
          </div>
          <div class="column col-auto col-sm-12 mb-2" style="align-self: flex-end">
            <button class="btn btn-primary btn-icon btn-icon-left btn-block"
                    :class="{loading}" :disabled="loading" @click="search">
              <fa-icon :icon="['fal', 'search']"/>Pesquisar
            </button>
          </div>
        </div>
        <div class="loading mt-2" v-if="loading"></div>
        <template v-else>
          <div class="empty mt-2" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Auditoria</p>
            <p class="empty-subtitle">
              Nenhum registro encontrado. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th style="width:140px">Data e hora</th>
              <th style="width:110px">Ação</th>
              <th style="width:180px">Registro</th>
              <th>Identificação</th>
              <th>Usuário responsável</th>
              <th style="width:60px" />
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td>{{ item.at | date('datetime') }}</td>
              <td>
                <span class="chip" :style="{ 'border-color': actionColor(item.action) }">
                  {{ actionName(item.action) }}
                </span>
              </td>
              <td>{{ entityName(item.entity) }}</td>
              <td>
                {{ item.label || '--' }}
                <div class="text-small text-gray" v-if="item.description">
                  {{ item.description }}
                </div>
              </td>
              <td>
                {{ actorName(item) }}
                <div class="text-small text-gray" v-if="item.origin">
                  {{ originName(item.origin.source) }}
                </div>
              </td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-gray tooltip"
                        data-tooltip="Detalhes" @click="openDetail(item)">
                  <fa-icon :icon="['fal', 'search']"/>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="card-footer" v-if="data.items.length > 0">
        <st-pagination :total="data.total"
                       :limit="data.limit" @paginate="paginate"></st-pagination>
        <span>Total de registros:
          <span class="ml-2 text-primary text-bold">{{ data.total }}</span>
        </span>
      </div>
    </div>
    <detail-modal
      v-if="detail.show"
      :show="detail.show"
      :item="detail.item"
      @close="detail.show = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, maxDate, minDate } from '@/data/validators';
import auditEntities, { getName as getEntityName } from '@/data/audit-entities';
import auditActions, { getName as getActionName, getColor } from '@/data/audit-actions';
import { getName as getOriginName } from '@/data/audit-origins';
import detailModal from './Detail.vue';

const MAX_PERIOD_DAYS = 30;

export default {
  components: {
    detailModal,
  },
  data() {
    return {
      path: '/audit-logs',
      loading: false,
      maxPeriodDays: MAX_PERIOD_DAYS,
      entities: auditEntities,
      actions: auditActions,
      filters: {
        startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        entity: '',
        action: '',
        search: '',
      },
      detail: {
        show: false,
        item: null,
      },
      data: {
        items: [],
        total: 0,
        limit: 30,
        offset: 0,
      },
    };
  },
  validations() {
    const rules = {
      filters: {
        startDate: { required, date },
        endDate: { required, date },
      },
    };

    if (this.filters.startDate && moment(this.filters.startDate).isValid()) {
      rules.filters.endDate = {
        required,
        date,
        minDate: minDate(this.filters.startDate),
        maxDate: maxDate(moment(this.filters.startDate).add(MAX_PERIOD_DAYS - 1, 'days')),
      };
    }

    return rules;
  },
  mounted() {
    this.load();
  },
  methods: {
    actionName: getActionName,
    actionColor: getColor,
    entityName: getEntityName,
    originName: getOriginName,
    actorName(item) {
      if (!item.actor) return '--';
      if (item.actor.name) return item.actor.name;
      return item.actor.system ? 'Sistema' : '--';
    },
    search() {
      this.$v.filters.$touch();
      if (this.$v.filters.$error) {
        return;
      }

      this.data.offset = 0;
      this.load();
    },
    load() {
      this.loading = true;

      const params = {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        limit: this.data.limit,
        offset: this.data.offset,
      };

      ['entity', 'action'].forEach((key) => {
        if (this.filters[key]) {
          params[key] = this.filters[key];
        }
      });

      if (this.filters.search.trim().length > 0) {
        params.search = this.filters.search.trim();
      }

      return this.$http.get(this.path, { params })
        .then(({ data }) => {
          this.data = data;
        })
        .catch(() => {})
        .then(() => {
          this.loading = false;
        });
    },
    openDetail(item) {
      this.detail.item = item;
      this.detail.show = true;
    },
    paginate({ offset }) {
      this.data.offset = offset;
      this.load();
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";
  .audits-page {
    .chip {
      background: transparent;
      border: 1px solid $gray-color;
      font-size: $font-size-sm;
    }
  }
</style>
